import './style/ProductDetails.css';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatDisplayNumber, formatNumberWithCommas, formatPercentWithCommas, formatToCurrency } from '../../util/Calculate';
import { useSelector } from 'react-redux';
import ScoreBar from './ScoreBar';
import Divider from './Divider';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from './Button';
import { Resize, Text, Button as NewButton, Navigate } from '@harperai/materialize-design-system';
import Loader from './Loader';

const ProductDetails = ({ productData }) => {
    const navigate = useNavigate();
    const { page, tabName } = useParams();

    const keystoneInterfaceIsLoggedIn = useSelector((state) => state?.keystoneInterface?.isLoggedIn);
    const keystoneInterfaceOffersAccepted = useSelector((state) => state?.keystoneInterface?.offersAccepted);
    const keystoneInventoryOffers = useSelector((state) => state?.keystoneInventory?.offers);

    const [productQuantity, setProductQuantity] = useState(productData?.quantity || 1);
    const [productUnitCost, setProductUnitCost] = useState(productData?.unit_value || 0.01);

    const { search } = useLocation();
    const selectedObjectId = new URLSearchParams(search).get('id') || null;

    useEffect(() => {
        if (productData) {
            setProductQuantity(productData?.quantity || 1);
            setProductUnitCost(productData?.unit_value || 0.01);
        }
    }, [productData]);

    const handleCloseTray = () => {
        const searchParams = new URLSearchParams(search);
        searchParams.delete('id');
        navigate(`/${page}?${searchParams.toString()}`);
    };

    const Specification = ({ key, name, specifications }) => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div key={key} className="flex vertical round-s grey-bg">
                <NewButton icon="plus" classes={`space-between`} rotateIcon={expanded} variant={`${expanded ? 'secondary' : 'transparent'}`} size="large" onClick={() => setExpanded(!expanded)}>{name}</NewButton>
                <Resize key={name} index={name} className="flex vertical gap-s" open={expanded}>
                    <div className="flex vertical pad-s gap-s">
                        {specifications[name].map((spec, index) => (
                            <div className={`${expanded ? "" : "display-none"} flex horizontal gap-l space-between align-start`} key={index + spec.name}>
                                <Text size="caption" variant="secondary" bold={true} classes="no-wrap" style={{flexGrow: 1}}>{spec.name}</Text>
                                <Text size="micro" mono={true}>{spec.value + " " + (((spec.units && spec.units !== "text" && spec.units !== "null" && !spec.units.includes('(') && !spec.units.includes(')')) ?  spec.units : ""))}</Text>
                            </div>
                        ))}
                    </div>
                </Resize>
            </div>
        );
    }

    const offers = useMemo(() => {
        return keystoneInventoryOffers?.filter(offer => offer.offer_items?.some(item => item.inventory_item?.id === selectedObjectId)).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)) ?? [];
    }, [keystoneInventoryOffers, selectedObjectId]);

    const shipments = useMemo(() => {
        return offers?.flatMap(offer => offer.shipments || []).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)) ?? [];
    }, [offers]);

    const payouts = useMemo(() => {
        return offers?.flatMap(offer => offer.payouts || []).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)) ?? [];
    }, [offers]);

    const noActivity = useMemo(() => {
        return offers?.length === 0 && shipments?.length === 0 && payouts?.length === 0;
    }, [offers, shipments, payouts]);

    const Offers = () => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div className="flex vertical">
                {!keystoneInterfaceOffersAccepted ? (
                    <div className={`product-horizontal-container align-center space-between pad-xs`}>
                        <Text size="paragraph" bold={true}>Offers</Text>
                        <Button label="Go to Setting" destinationUrl={"/manage/organization"} brand={true} />
                    </div>
                ) : (
                    <div key="offer-section" className="flex vertical round-s grey-bg">
                        <NewButton icon="plus" classes={`space-between`} rotateIcon={expanded} disabled={offers?.length === 0} variant={`${expanded ? 'secondary' : 'transparent'}`} size="large" onClick={() => setExpanded(!expanded)}>Offers ({offers?.length})</NewButton>
                        <Resize key="offer-rows" index="offer-rows" className="flex vertical gap-s" open={expanded}>
                            <div className="flex vertical pad-s gap-s">
                                {offers.map((offer, index) => (
                                    <Navigate key={offer.id} href={`/offers/${offer.status === "AWAITING_RESPONSE" ? "active" : offer.status === "ACCEPTED" ? "accepted" : offer.status === "COMPLETED" ? "completed" : "rejected"}?id=${offer.id}`} variant="secondary" unsetHeight={true} unsetPadding={true} classes="space-between">
                                        <div className="flex vertical align-start">
                                                <Text size="paragraph" mono={true} bold={true}>{formatDisplayNumber(offer.offer_number)}</Text>
                                                <Text size="caption" variant="secondary" bold={true}>Items: {offer.offer_items?.reduce((acc, item) => acc + item.quantity, 0)} • Locations: {offer.unique_locations?.length}</Text>
                                        </div>
                                        <div className='product-horizontal-container justify-end align-center inventory-column strict-width last'>
                                        <div className="status-chip flex horizontal grey-bg round-s gap-m">
                                            <div class={`small-circle ${offer.status === "AWAITING_RESPONSE" ? "circle-warning" : offer.status === "ACCEPTED" ? "circle-positive" : offer.status === "COMPLETED" ? "circle-positive" : "circle-negative"}`}/>
                                            <Text size="micro" bold={true} mono={true}>{offer.status === "AWAITING_RESPONSE" ? "Awaiting Response" : offer.status === "ACCEPTED" ? "Accepted" : offer.status === "COMPLETED" ? "Completed" : "Rejected"}</Text>
                                        </div>
                                        </div>
                                    </Navigate>
                                ))}
                            </div>
                        </Resize>
                    </div>
                )}
            </div>
        )
    }

    const Shipments = () => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div className="flex vertical">
                <div key="shipment-section" className="flex vertical round-s grey-bg">
                    <NewButton icon="plus" classes={`space-between`} rotateIcon={expanded} variant={`${expanded ? 'secondary' : 'transparent'}`} size="large" disabled={shipments?.length === 0} onClick={() => setExpanded(!expanded)}>Shipments ({shipments?.length})</NewButton>
                    <Resize key="shipment-rows" index="shipment-rows" className="flex vertical gap-s" open={expanded}>
                        <div className="flex vertical pad-s gap-s">
                            {shipments.map((shipment, index) => (
                                <Navigate key={shipment.id} href={`/shipments/${shipment.status === "PENDING" ? "awaiting-shipment" : "shipped"}?id=${shipment.id}`} variant="secondary" unsetHeight={true} unsetPadding={true} classes="space-between">
                                    <div className="flex vertical">
                                            <Text size="paragraph" mono={true} bold={true}>{formatDisplayNumber(shipment.shipment_number)}</Text>
                                            <Text size="caption" variant="secondary" bold={true}>Warehouse: {shipment.warehouse_name}</Text>
                                    </div>
                                    <div className='product-horizontal-container justify-end align-center inventory-column strict-width last'>
                                    <div className="status-chip flex horizontal grey-bg round-s gap-m">
                                        <div class={`small-circle circle-${shipment.status === "PENDING" ? "negative" : shipment.status === "SHIPPED" ? "warning" : "positive"}`}/>
                                        <Text size="micro" bold={true} mono={true}>{shipment.status === "PENDING" ? "Awaiting Shipment" : shipment.status === "SHIPPED" ? "Shipped" : "Delivered"}</Text>
                                    </div>
                                    </div>
                                </Navigate>
                            ))}
                        </div>
                    </Resize>
                </div>
            </div>
        )
    }

    const Payouts = () => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div className="flex vertical">
                <div key="payout-section" className="flex vertical round-s grey-bg">
                    <NewButton icon="plus" classes={`space-between`} rotateIcon={expanded} disabled={payouts?.length === 0} variant={`${expanded ? 'secondary' : 'transparent'}`} size="large" onClick={() => setExpanded(!expanded)}>Payouts ({payouts?.length})</NewButton>
                    <Resize key="payout-rows" index="payout-rows" className="flex vertical gap-s" open={expanded}>
                        <div className="flex vertical pad-s gap-s">
                            {payouts.map((payout, index) => (
                                <Navigate key={payout.id} href={`/payouts/${payout.status === "ON_HOLD" ? "on-hold" : "sent"}?id=${payout.id}`} variant="secondary" unsetHeight={true} unsetPadding={true} classes="space-between">
                                    <div className="flex vertical">
                                            <Text size="paragraph" mono={true} bold={true}>{formatDisplayNumber(payout.payout_number)}</Text>
                                            <Text size="caption" variant="secondary" bold={true}>Total: {formatToCurrency(payout.amount)}</Text>
                                    </div>
                                    <div className='product-horizontal-container justify-end align-center inventory-column strict-width last'>
                                    <div className="status-chip flex horizontal grey-bg round-s gap-m">
                                        <div class={`small-circle circle-${payout.status === "ON_HOLD" ? "warning" : "positive"}`}/>
                                        <Text size="micro" bold={true} mono={true}>{payout.status === "ON_HOLD" ? "On Hold" : "Sent"}</Text>
                                    </div>
                                    </div>
                                </Navigate>
                            ))}
                        </div>
                    </Resize>
                </div>
            </div>
        )
    }

    const Listing = () => {
        let hasShopListing = productData?.shop_listing !== null;
        // hasShopListing = false;

        let hasUpleveledItem = productData?.upleveled_item !== null;
        // hasUpleveledItem = false;

        const manufacturer = hasShopListing ? productData?.shop_listing?.market_manufacturer : productData?.upleveled_item?.manufacturer;
        const partNumber = hasShopListing ? productData?.shop_listing?.market_mpn : productData?.upleveled_item?.mpn;
        const description = hasShopListing ? productData?.shop_listing?.description : productData?.upleveled_item?.short_description;
        const specifications = hasShopListing ? productData?.shop_listing?.specifications_by_type : productData?.upleveled_item?.specifications_by_type;

        return (
            <div className="keystone-card">
                <div key="listing" className="product-vertical-container">
                    <div className="flex vertical">
                        <div className="product-vertical-container gap-m pad-m">
                            <div className="flex horizontal align-center space-between">
                                <div className="flex horizontal align-center gap-m">
                                    <Text header={3} size="title" bold={true}>Listing</Text>
                                    {(hasShopListing || hasUpleveledItem) && (
                                        <div className="status-chip flex horizontal grey-bg round-s gap-m">
                                            {(hasShopListing || hasUpleveledItem) && (
                                                <div class={`small-circle ${hasShopListing ? "circle-positive" : "circle-warning"}`}/>
                                            )}
                                            <Text size="micro" bold={true} mono={true}>{hasShopListing ? "Live" : (manufacturer && partNumber && description) ? "In Review" : "Pending"}</Text>
                                        </div>
                                    )}
                                </div>
                                <Navigate size="medium" variant="secondary" icon="external" href={productData?.shop_listing_url} newTab={true} disabled={!hasShopListing}>View Listing</Navigate>
                            </div>
                        </div>
                        {(manufacturer && partNumber && description) && (
                            <>
                                <Divider />
                                <div className="flex vertical pad-m gap-l">
                                    <div className="flex vertical gap-s">
                                        <div className="flex vertical">
                                            <Text bold={true} size="caption" variant="secondary">{manufacturer}</Text>
                                            <Text bold={true} size="h6" mono={true}>{partNumber}</Text>
                                        </div>
                                        <Text size="caption" mono={true}>{description}</Text>
                                    </div>
                                    {(specifications && Object.keys(specifications).length > 0) && (
                                        <div className="flex vertical gap-s">
                                            <Text bold={true}>Specifications</Text>
                                            {Object.keys(specifications).map((key) => (
                                                <Specification key={key} name={key} specifications={specifications} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    {(!manufacturer || !partNumber && !description) && (
                        <Loader loading={true} absolute={false} small={true} showLoadingText={true} loadingTextOptions={["We're working on matching this part to market data, check back soon."]} />
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="keystone-offer-details product-vertical-container flex-1 width-100">
            <div className="keystone-tray-header black-bg product-vertical-container width-100 space-between pad-l v-gap-display">
                <div className="flex horizontal space-between">
                    <Button
                        label="Dismiss"
                        onClick={handleCloseTray}
                        textColor="white"
                        onBrand={true} />
                    <Text size="caption" bold={true} variant="white" classes="align-end ellipsis" mono={true}>Unique ID: {productData?.unique_id}</Text>
                </div>
                <div className="product-vertical-container gap-m">
                    <div className="flex vertical gap-s">
                        <div className="flex vertical">
                            <Text size="paragraph" bold={true} variant="white">{productData?.manufacturer || productData?.upleveled_item?.manufacturer}</Text>
                            <Text size="h1" bold={true} variant="yellow" mono={true}>{productData?.mpn || productData?.upleveled_item?.mpn}</Text>
                        </div>
                        <Text size="caption" variant="white" mono={true} bold={true}>{productData?.description}</Text>
                    </div>
                </div>
            </div>
            <div className="product-vertical-container flex-1 width-100 overflow-auto">
                <div className="product-vertical-container flex-1 grey-bg width-100 pad-m v-gap-m overflow-auto">
                    <div id="product-example-header" className="product-vertical-container">
                        <div className="product-vertical-container h-gap-xl align-center">
                            <div className="product-vertical-container flex-1 width-100 v-gap-xl">
                                <div className="product-vertical-container flex-1 keystone-card width-100">
                                    <div className="product-vertical-container flex-1 width-100 pad-m v-gap-l">
                                        <div className="product-horizontal-container flex-1 width-100 align-center space-between">
                                            <Text header={3} size="title" bold={true}>Your Stock</Text>
                                            <Text size="caption" bold={true} mono={true}>Warehouse: {productData?.warehouse_name}</Text>
                                        </div>
                                        <div className="product-horizontal-container gap-m flex-1 width-100 space-between flex-wrap">
                                            <div className="flex grow-1 vertical grey-bg pad-s round-m">
                                                <Text bold={true} size="h6">{formatNumberWithCommas(productQuantity)}</Text>
                                                <Text bold={true} variant="secondary" size="caption">Quantity</Text>
                                            </div>
                                            <div className="flex grow-1 vertical grey-bg pad-s round-m">
                                                <Text bold={true}  size="h6">{formatToCurrency(productUnitCost)}</Text>
                                                <Text bold={true} variant="secondary" size="caption">Asking Price</Text>
                                            </div>
                                            <div className="flex grow-1 vertical grey-bg pad-s round-m">
                                                <Text bold={true} size="h6">{formatToCurrency(productUnitCost * productQuantity)}</Text>
                                                <Text bold={true} variant="secondary" size="caption">Total Asking</Text>
                                            </div>
                                        </div>
                                        {keystoneInterfaceIsLoggedIn && <ScoreBar score={productData?.computed?.percentage_of_total_min_price} label={formatPercentWithCommas(productData?.computed?.percentage_of_total_min_price) + " Of Inventory Value"} minLabel="" maxLabel="" />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Listing />
                    <div className="flex vertical pad-m keystone-card gap-m">
                        <Text bold={true} size="title">Activity</Text>
                        <div className="flex vertical gap-s">
                            <Offers />
                            <Shipments />
                            <Payouts />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(ProductDetails);
